// Version Service for Recovery Commons
// Provides version information, build details, and environment data

import { getEnvironment, isDevelopment, isStaging, isProduction } from '../config/constants';

// Version information interface
export interface VersionInfo {
  version: string;
  buildDate: string;
  environment: string;
  environmentType: 'development' | 'staging' | 'production';
  gitCommit?: string;
  gitBranch?: string;
  lastDeployed?: string;
}

// Get version info from version-info.json file
const getVersionFromFile = async (): Promise<any> => {
  try {
    const response = await fetch('/version-info.json');
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn('Could not load version-info.json:', error);
  }
  return null;
};

// Get package.json version
const getPackageVersion = async (): Promise<string> => {
  try {
    // Try to get from version-info.json first
    const versionInfo = await getVersionFromFile();
    if (versionInfo?.version) {
      return versionInfo.version;
    }
    
    // Fallback to environment variable
    return process.env.REACT_APP_VERSION || '1.0.0';
  } catch {
    return '1.0.0';
  }
};

// Get build date
const getBuildDate = async (): Promise<string> => {
  try {
    // Try to get from version-info.json first
    const versionInfo = await getVersionFromFile();
    if (versionInfo?.buildDate) {
      return new Date(versionInfo.buildDate).toLocaleString();
    }
    
    // Fallback to environment variable
    const buildDate = process.env.REACT_APP_BUILD_DATE || new Date().toISOString();
    return new Date(buildDate).toLocaleString();
  } catch {
    return new Date().toLocaleString();
  }
};

// Get git information (if available)
const getGitInfo = async () => {
  try {
    // Try to get from version-info.json first
    const versionInfo = await getVersionFromFile();
    if (versionInfo) {
      return {
        commit: versionInfo.gitCommit || undefined,
        branch: versionInfo.gitBranch || undefined
      };
    }
    
    // Fallback to environment variables
    return {
      commit: process.env.REACT_APP_GIT_COMMIT || undefined,
      branch: process.env.REACT_APP_GIT_BRANCH || undefined
    };
  } catch {
    return {
      commit: undefined,
      branch: undefined
    };
  }
};

// Get last deployed timestamp
const getLastDeployed = async (): Promise<string> => {
  try {
    // Try to get from version-info.json first
    const versionInfo = await getVersionFromFile();
    if (versionInfo?.lastDeployed) {
      return new Date(versionInfo.lastDeployed).toLocaleString();
    }
    
    // Fallback to environment variable
    const lastDeployed = process.env.REACT_APP_LAST_DEPLOYED || new Date().toISOString();
    return new Date(lastDeployed).toLocaleString();
  } catch {
    return new Date().toLocaleString();
  }
};

// Get environment type with more descriptive names
const getEnvironmentType = (): 'development' | 'staging' | 'production' => {
  const env = getEnvironment();
  if (env === 'development') return 'development';
  if (env === 'staging') return 'staging';
  return 'production';
};

// Get environment display name
const getEnvironmentDisplayName = (): string => {
  const env = getEnvironment();
  switch (env) {
    case 'development':
      return 'Local Development';
    case 'staging':
      return 'Development Environment';
    case 'production':
      return 'Production';
    default:
      return 'Unknown';
  }
};

// Main function to get version information
export const getVersionInfo = async (): Promise<VersionInfo> => {
  const gitInfo = await getGitInfo();
  
  return {
    version: await getPackageVersion(),
    buildDate: await getBuildDate(),
    environment: getEnvironmentDisplayName(),
    environmentType: getEnvironmentType(),
    gitCommit: gitInfo.commit,
    gitBranch: gitInfo.branch,
    lastDeployed: await getLastDeployed()
  };
};

// Get environment badge color
export const getEnvironmentBadgeColor = (): string => {
  if (isDevelopment()) return '#6c757d'; // Gray for local
  if (isStaging()) return '#fd7e14';     // Orange for dev
  return '#28a745';                      // Green for production
};

// Get environment badge text
export const getEnvironmentBadgeText = (): string => {
  if (isDevelopment()) return 'LOCAL';
  if (isStaging()) return 'DEV';
  return 'PROD';
};

// Check if this is a production build
export const isProductionBuild = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

// Get deployment info for debugging
export const getDeploymentInfo = () => {
  return {
    nodeEnv: process.env.NODE_ENV,
    isProductionBuild: isProductionBuild(),
    environment: getEnvironment(),
    version: getVersionInfo()
  };
};
